import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/components/ProtectedRoute';
import { PageLayout } from '@/components/layout';
import Callback from '@/pages/Callback';
import Dashboard from '@/pages/Dashboard';
import Settings from '@/pages/Settings';
import Playlists from '@/pages/Playlists';
import Analytics from '@/pages/Analytics';
import { ToastProvider } from '@/contexts/ToastContext';

/**
 * Login Page
 */
const Login: React.FC = () => {
  const handleLogin = () => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiUrl}/api/auth/login`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-spotify-black to-gray-900 flex items-center justify-center px-4">
      <div className="text-center max-w-md animate-fade-in">
        <div className="mb-6">
          <span className="text-7xl md:text-8xl animate-bounce-subtle inline-block">🎵</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gradient-green mb-3">
          Auto Music
        </h1>
        <p className="text-lg md:text-xl text-gray-400 mb-8">
          Intelligent music discovery powered by Spotify
        </p>
        <button
          onClick={handleLogin}
          className="btn-primary text-lg px-8 py-3 hover-lift hover-glow press-effect"
        >
          Connect with Spotify
        </button>
        <div className="mt-8 flex items-center justify-center gap-6 text-gray-500 text-sm">
          <span className="flex items-center gap-1.5">🤖 AI-Powered</span>
          <span className="flex items-center gap-1.5">🎭 Mood-Based</span>
          <span className="flex items-center gap-1.5">🔍 Discovery</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Dashboard with PageLayout
 */
const DashboardPage: React.FC = () => (
  <PageLayout maxWidth="md" showBreadcrumbs={false}>
    <Dashboard />
  </PageLayout>
);

/**
 * Settings with PageLayout
 */
const SettingsPage: React.FC = () => (
  <PageLayout maxWidth="xl">
    <Settings />
  </PageLayout>
);

/**
 * Playlists with PageLayout
 */
const PlaylistsPage: React.FC = () => (
  <PageLayout maxWidth="xl">
    <Playlists />
  </PageLayout>
);

/**
 * Analytics with PageLayout
 */
const AnalyticsPage: React.FC = () => (
  <PageLayout maxWidth="xl">
    <Analytics />
  </PageLayout>
);

/**
 * Main App Component
 */
function App() {
  return (
    <ToastProvider position="bottom-center">
      <Router basename="/automusic">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/playlists" element={
            <ProtectedRoute>
              <PlaylistsPage />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </ToastProvider>
  );
}

export default App;
